import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuItem } from '@mui/material';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { DecimalInput } from 'components/Form/DecimalInput/DecimalInput';
import { Input } from 'components/Form/Input/Input';
import { InputGroup } from 'components/Form/InputGroup/InputGroup';
import { InputGroupDivider } from 'components/Form/InputGroup/InputGroupDivider';
import { InputGroupSelect } from 'components/Form/InputGroup/InputGroupSelect';
import { Label } from 'components/Form/Label/Label';
import { RadioGroup } from 'components/Form/RadioGroup/RadioGroup';
import { Select } from 'components/Form/Select/Select';
import { Button } from 'components/UI/Button/Button';
import { Card } from 'components/UI/Card/Card';
import { CardContent } from 'components/UI/Card/Content/CardContent';
import { CardHeader } from 'components/UI/Card/Header/CardHeader';
import { Switch } from 'components/UI/Switch/Switch';
import { useMarketplace } from 'hooks/useMarketplace';
import { useMasterData } from 'hooks/useMasterData';
import { Moment } from 'moment';
import React from 'react';
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Feeding } from 'types/OnlineBooking';
import { getFoodMaxDecimals } from 'utils/dosageDecimalsConfig';
import styles from './FeedingForm.module.scss';

interface FeedingFormData {
    feedings: Array<Feeding>;
}

interface FoodQuantityDecimalInputProps {
    control: Control<FeedingFormData>;
    feedingIndex: number;
    units: Array<{ id: number; name: string }>;
    value: number | string | undefined;
    onChange: (v: number) => void;
}

const FoodQuantityDecimalInput: React.FC<FoodQuantityDecimalInputProps> = ({
    control,
    feedingIndex,
    units,
    value,
    onChange
}) => {
    const unitId = useWatch({ control, name: `feedings.${feedingIndex}.quantity.unit` as const });
    const unitName = units.find(u => u.id === unitId)?.name;
    const scale = getFoodMaxDecimals(unitName);

    return (
        <DecimalInput
            value={value}
            onChange={onChange}
            scale={scale}
            placeholder={scale > 0 ? `Ex. 0.${'5'.padEnd(scale, '0').slice(0, scale)}` : 'Ex. 1'}
        />
    );
};

interface FeedingFormProps {
    value?: Array<Feeding>;
    onChange?: (feedings: Array<Feeding>) => void;
    required?: boolean;
    bookingStartDate?: Moment;
    bookingEndDate?: Moment;
}

export const FeedingForm: React.FC<FeedingFormProps> = ({
    value = [],
    onChange,
    required,
    bookingStartDate,
    bookingEndDate
}) => {
    const { masterData } = useMasterData();
    const marketplace = useMarketplace();
    const [isFeedingEnabled, setIsFeedingEnabled] = React.useState(required || value.length > 0);
    const onChangeRef = React.useRef(onChange);
    const hasAutoAddedRef = React.useRef(false);

    // Keep ref updated
    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Get marketplace feeding settings for a moment type by name
    const getFeedingSettingsForMoment = React.useCallback(
        (
            name: string
        ): {
            enabled: boolean;
            time: { hour: number; minute: number } | null;
        } => {
            const settings = marketplace.settings;
            const lower = name.toLowerCase();
            if (lower.includes('morning')) {
                return {
                    enabled: settings.morningFeedingEnabled,
                    time: settings.morningFeedingTime
                };
            }
            if (lower.includes('afternoon')) {
                return {
                    enabled: settings.afternoonFeedingEnabled,
                    time: settings.afternoonFeedingTime
                };
            }
            if (lower.includes('evening')) {
                return {
                    enabled: settings.eveningFeedingEnabled,
                    time: settings.eveningFeedingTime
                };
            }
            return { enabled: true, time: null }; // Default for unknown types
        },
        [marketplace.settings]
    );

    // Filter and adjust feeding moment types based on marketplace settings
    const feedingMomentTypes = React.useMemo(() => {
        return masterData.feedingMomentTypes
            .filter(momentType => {
                const settings = getFeedingSettingsForMoment(momentType.name);
                return settings.enabled;
            })
            .map(momentType => {
                const settings = getFeedingSettingsForMoment(momentType.name);
                // Use marketplace time if set, otherwise use masterdata default
                if (settings.time) {
                    return {
                        ...momentType,
                        hour: settings.time.hour,
                        minute: settings.time.minute
                    };
                }
                return momentType;
            });
    }, [masterData.feedingMomentTypes, getFeedingSettingsForMoment]);

    // Get window intervals: [start, end) where end is the next window's start or 23:59
    const getWindowIntervals = React.useCallback(() => {
        const sorted = [...feedingMomentTypes].sort((a, b) => {
            const aMinutes = a.hour * 60 + a.minute;
            const bMinutes = b.hour * 60 + b.minute;
            return aMinutes - bMinutes;
        });

        return sorted.map((moment, idx) => {
            const startMinutes = moment.hour * 60 + moment.minute;
            // End is next window's start, or end of day (23:59 = 1439 minutes)
            const endMinutes =
                idx < sorted.length - 1
                    ? sorted[idx + 1].hour * 60 + sorted[idx + 1].minute
                    : 24 * 60 - 1; // 23:59

            return {
                id: moment.id,
                name: moment.name,
                hour: moment.hour,
                minute: moment.minute,
                startMinutes,
                endMinutes
            };
        });
    }, [feedingMomentTypes]);

    // Check if two intervals intersect: [a1, a2) ∩ [b1, b2) ≠ ∅
    // Using start-inclusive, end-exclusive logic
    const intervalsIntersect = (a1: number, a2: number, b1: number, b2: number): boolean => {
        // Intervals intersect if: a1 < b2 AND b1 < a2
        return a1 < b2 && b1 < a2;
    };

    // Check if a feeding window is eligible based on booking dates
    const isWindowEligible = React.useCallback(
        (momentId: number): boolean => {
            if (!bookingStartDate || !bookingEndDate) return true; // No filtering if dates not set

            const windowIntervals = getWindowIntervals();
            const window = windowIntervals.find(w => w.id === momentId);
            if (!window) return true;

            const startDate = bookingStartDate.clone().startOf('day');
            const endDate = bookingEndDate.clone().startOf('day');
            const isSameDay = startDate.isSame(endDate, 'day');

            if (isSameDay) {
                // Daycare: Check if [DropOff, PickUp) intersects [WindowStart, WindowEnd)
                const dropOffMinutes = bookingStartDate.hour() * 60 + bookingStartDate.minute();
                const pickUpMinutes = bookingEndDate.hour() * 60 + bookingEndDate.minute();

                return intervalsIntersect(
                    dropOffMinutes,
                    pickUpMinutes,
                    window.startMinutes,
                    window.endMinutes
                );
            } else {
                // Boarding (multi-day): Window is eligible if eligible on any day
                const dropOffMinutes = bookingStartDate.hour() * 60 + bookingStartDate.minute();
                const pickUpMinutes = bookingEndDate.hour() * 60 + bookingEndDate.minute();
                const endOfDay = 24 * 60 - 1; // 23:59

                // First day: [DropOff, EndOfDay) ∩ Window
                const firstDayEligible = intervalsIntersect(
                    dropOffMinutes,
                    endOfDay,
                    window.startMinutes,
                    window.endMinutes
                );

                // Last day: WindowStart < PickUp (window must start before pickup)
                const lastDayEligible = window.startMinutes < pickUpMinutes;

                // Middle days: All windows are eligible (if there are middle days)
                const totalDays = endDate.diff(startDate, 'days');
                const hasMiddleDays = totalDays > 1;

                return firstDayEligible || lastDayEligible || hasMiddleDays;
            }
        },
        [bookingStartDate, bookingEndDate, getWindowIntervals]
    );

    const defaultFeeding: Feeding = React.useMemo(
        () => ({
            momentTypes: [],
            foodProvisionType: masterData.foodProvisionTypes[0]?.id || 0,
            foodType: 0,
            quantity: {
                quantity: NaN,
                unit: masterData.foodQuantityUnits[0]?.id || 0
            }
        }),
        [masterData.foodProvisionTypes, masterData.foodQuantityUnits]
    );

    // Ensure all feedings have proper default values
    const normalizeFeeding = (feeding: Feeding): Feeding => ({
        momentTypes: feeding.momentTypes || [],
        foodProvisionType: feeding.foodProvisionType ?? masterData.foodProvisionTypes[0]?.id ?? 0,
        foodType: feeding.foodType ?? masterData.foodTypes[0]?.id ?? 0,
        quantity: {
            quantity: feeding.quantity?.quantity ?? 0,
            unit: feeding.quantity?.unit ?? masterData.foodQuantityUnits[0]?.id ?? 0
        },
        instructions: feeding.instructions
    });

    const initialFeedings =
        value.length > 0 ? value.map(normalizeFeeding) : required ? [defaultFeeding] : [];

    const { control, watch, setValue } = useForm<FeedingFormData>({
        defaultValues: {
            feedings: initialFeedings
        },
        mode: 'onChange'
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'feedings'
    });

    // Watch all form values and call onChange when they change
    React.useEffect(() => {
        const subscription = watch(data => {
            if (onChangeRef.current && data.feedings && (required || isFeedingEnabled)) {
                onChangeRef.current(data.feedings as Array<Feeding>);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, required, isFeedingEnabled]);

    // Notify parent when feeding is disabled (only if not required)
    React.useEffect(() => {
        if (!required && !isFeedingEnabled && onChangeRef.current) {
            onChangeRef.current([]);
        }
    }, [required, isFeedingEnabled]);

    // Auto-add feeding item when enabled (only if not required)
    React.useEffect(() => {
        if (!required && isFeedingEnabled && fields.length === 0 && !hasAutoAddedRef.current) {
            hasAutoAddedRef.current = true;
            append(defaultFeeding);
        }

        // Reset the ref when feeding is disabled
        if (!isFeedingEnabled) {
            hasAutoAddedRef.current = false;
        }
    }, [isFeedingEnabled, fields.length, required, append, defaultFeeding]);

    // Clear brand field when food provision type changes away from 2
    React.useEffect(() => {
        const subscription = watch((data, { name }) => {
            if (name?.startsWith('feedings.') && name.endsWith('.foodProvisionType')) {
                const match = name.match(/feedings\.(\d+)\.foodProvisionType/);
                if (match) {
                    const index = parseInt(match[1], 10);
                    const foodProvisionType = data.feedings?.[index]?.foodProvisionType;
                    if (foodProvisionType !== 2) {
                        setValue(`feedings.${index}.brand`, undefined);
                    }
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    const handleAddFeeding = () => {
        append(defaultFeeding);
    };

    const handleRemoveFeeding = (index: number) => {
        if (required && fields.length === 1) {
            return; // Don't allow removing the last feeding if required
        }
        remove(index);

        // Auto-disable switch when last feeding is deleted (only if not required)
        if (!required && fields.length === 1) {
            setIsFeedingEnabled(false);
        }
    };

    const toggleMomentType = (
        fieldIndex: number,
        momentTypeId: number,
        currentValues: number[]
    ) => {
        const isSelected = currentValues.includes(momentTypeId);
        if (isSelected) {
            return currentValues.filter(id => id !== momentTypeId);
        } else {
            return [...currentValues, momentTypeId];
        }
    };

    // If not required and feeding is disabled, show toggle header
    if (!required && !isFeedingEnabled) {
        return (
            <div className="flex flex-col gap-4 items-start">
                <h4 className="h4 w-full flex items-center gap-2">
                    <Switch checked={false} onCheckedChange={setIsFeedingEnabled} />
                    Feeding
                </h4>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 items-start">
            <div className="flex flex-col gap-6 w-full">
                {fields.map((field, index) => {
                    const currentFoodProvisionType = watch(`feedings.${index}.foodProvisionType`);

                    return (
                        <div key={field.id} className="w-full flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <h4 className="h4 flex items-center gap-2">
                                    {!required && index === 0 && (
                                        <Switch
                                            checked={true}
                                            onCheckedChange={setIsFeedingEnabled}
                                        />
                                    )}
                                    Feeding {index + 1}
                                </h4>

                                {index > 0 && (
                                    <Button
                                        variant="link"
                                        onClick={() => handleRemoveFeeding(index)}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </Button>
                                )}
                            </div>
                            <Card>
                                <CardHeader className={styles.cardHeader}>
                                    <p className="h4">Feeding Schedule</p>

                                    <div className={styles.momentTypesContainer}>
                                        <Controller
                                            name={`feedings.${index}.momentTypes`}
                                            control={control}
                                            rules={{
                                                validate: value =>
                                                    value.length > 0 ||
                                                    'At least one moment type is required'
                                            }}
                                            render={({ field: { value, onChange } }) => (
                                                <>
                                                    {feedingMomentTypes.map(momentType => {
                                                        const eligible = isWindowEligible(
                                                            momentType.id
                                                        );
                                                        return (
                                                            <Checkbox
                                                                key={momentType.id}
                                                                label={momentType.name}
                                                                className={styles.checkbox}
                                                                checked={value.includes(
                                                                    momentType.id
                                                                )}
                                                                disabled={!eligible}
                                                                onChange={() =>
                                                                    onChange(
                                                                        toggleMomentType(
                                                                            index,
                                                                            momentType.id,
                                                                            value
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </>
                                            )}
                                        />
                                    </div>
                                </CardHeader>

                                <CardContent className={styles.cardContent}>
                                    <Controller
                                        name={`feedings.${index}.foodProvisionType`}
                                        control={control}
                                        rules={{ required: 'Food provision type is required' }}
                                        render={({ field: { value, onChange } }) => (
                                            <RadioGroup
                                                label="Food Provided"
                                                options={masterData.foodProvisionTypes.map(
                                                    type => ({
                                                        id: type.id.toString(),
                                                        name: type.name
                                                    })
                                                )}
                                                value={value.toString()}
                                                onChange={val => onChange(Number(val.id))}
                                                className={styles.radioGroup}
                                            />
                                        )}
                                    />

                                    <div className={styles.foodTypeContainer}>
                                        <div className="flex flex-col flex-1">
                                            <Label>Food Type</Label>
                                            <Controller
                                                name={`feedings.${index}.foodType`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Select
                                                        placeholder="Select Food Type"
                                                        options={masterData.foodTypes.map(type => ({
                                                            id: type.id.toString(),
                                                            name: type.name
                                                        }))}
                                                        value={value?.toString() || ''}
                                                        onChange={val =>
                                                            onChange(
                                                                val ? Number(val.id) : undefined
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <Label>Food Qty</Label>
                                            <InputGroup className={styles.foodQtyInputGroup}>
                                                <Controller
                                                    name={`feedings.${index}.quantity.quantity`}
                                                    control={control}
                                                    rules={{
                                                        required: 'Quantity is required',
                                                        min: {
                                                            value: 0.001,
                                                            message: 'Minimum 0.001'
                                                        }
                                                    }}
                                                    render={({ field: { value, onChange } }) => (
                                                        <FoodQuantityDecimalInput
                                                            control={control}
                                                            feedingIndex={index}
                                                            units={masterData.foodQuantityUnits}
                                                            value={value}
                                                            onChange={onChange}
                                                        />
                                                    )}
                                                />
                                                <InputGroupDivider />
                                                <Controller
                                                    name={`feedings.${index}.quantity.unit`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <InputGroupSelect
                                                            value={value.toString()}
                                                            onChange={e =>
                                                                onChange(Number(e.target.value))
                                                            }
                                                        >
                                                            {masterData.foodQuantityUnits.map(
                                                                unit => (
                                                                    <MenuItem
                                                                        key={unit.id}
                                                                        value={unit.id.toString()}
                                                                    >
                                                                        {unit.name}
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </InputGroupSelect>
                                                    )}
                                                />
                                            </InputGroup>
                                        </div>
                                    </div>

                                    {currentFoodProvisionType === 2 && (
                                        <div className="flex flex-col flex-1">
                                            <Label>Brand</Label>
                                            <Controller
                                                name={`feedings.${index}.brand`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Input
                                                        className="mt-0"
                                                        value={value || ''}
                                                        placeholder="Ex. Royal Canin, Farmer's Dog, Hil..."
                                                        onChange={e => onChange(e.target.value)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}

                                    <div className="flex flex-col">
                                        <Label>Feeding Instructions</Label>
                                        <Controller
                                            name={`feedings.${index}.instructions`}
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <Input
                                                    placeholder="Ex. Feed separately from other pets"
                                                    className="mt-0"
                                                    value={value || ''}
                                                    onChange={e => onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    );
                })}
            </div>

            {(required || isFeedingEnabled) && (
                <Button variant="link" onClick={handleAddFeeding} type="button">
                    <FontAwesomeIcon icon={faPlus} />
                    <span className="underline">Add feeding</span>
                </Button>
            )}
        </div>
    );
};
